// Per-tenant color for the merged WhatsApp thread view (rail, clinic label chip, avatar badge,
// reply-from picker). One stable hue per tenant, reusing the calendar's `hashHue` so a clinic's
// color here matches its color on the calendar and slot heatmap. Everything is derived from that
// single hue at a fixed OKLCH lightness/chroma, echoing the design reference
// (Camden/Toa Payoh/Bedok sat around oklch(0.6 0.12 <hue>)).
//
// Theme-awareness is delivered as CSS custom properties (see {@link tenantColorVars}): the rail /
// badge solid stays constant across light and dark (per the design handoff), while the tint
// background and the label text swap to dark-appropriate values via Tailwind `dark:` variants that
// read the *-dark properties. Callers set the vars on a segment root once and reference them with
// utilities like `bg-(--seg-tint) dark:bg-(--seg-tint-dark)`.

import { hashHue } from "@/components/pages/calendar/slots-heatmap/heatmap-utils";

// Fixed OKLCH lightness/chroma for the solid accent — the middle of the reference swatches, vivid
// enough to read as a rail yet calm behind text.
const SOLID_L = 0.62;
const SOLID_C = 0.13;

/**
 * The full color set for a tenant, as ready-to-use CSS color strings.
 * @param {string} tenantId Stable tenant/clinic id (falls back to hashing whatever string is given).
 */
export function tenantSegmentColors(tenantId) {
  const hue = hashHue(tenantId);
  return {
    hue,
    // Solid rail bar, avatar badge dot, picker dot — identical in both themes.
    solid: `oklch(${SOLID_L} ${SOLID_C} ${hue})`,
    // Clinic label chip / picker pill background: a faint wash of the hue. Slightly stronger in
    // dark mode so it survives the darker surface.
    tint: `oklch(${SOLID_L} ${SOLID_C} ${hue} / 0.10)`,
    tintDark: `oklch(${SOLID_L} ${SOLID_C} ${hue} / 0.16)`,
    // Label / pill text: a darkened variant reads on the light wash; a lightened one on the dark.
    label: `oklch(0.45 ${SOLID_C} ${hue})`,
    labelDark: `oklch(0.82 0.10 ${hue})`,
    // Hairline border for the chip / pill, same hue at low alpha.
    border: `oklch(${SOLID_L} ${SOLID_C} ${hue} / 0.35)`,
  };
}

/**
 * Inline `style` object of CSS custom properties for a merged-view segment root. Set once on the
 * segment container; descendants reference the vars via Tailwind arbitrary utilities, e.g.
 *   className="bg-(--seg-tint) dark:bg-(--seg-tint-dark) text-(--seg-label) dark:text-(--seg-label-dark)"
 * and the rail/badge use `bg-(--seg-solid)`.
 * @param {string} tenantId
 * @returns {import("react").CSSProperties}
 */
export function tenantColorVars(tenantId) {
  const c = tenantSegmentColors(tenantId);
  return {
    "--seg-solid": c.solid,
    "--seg-tint": c.tint,
    "--seg-tint-dark": c.tintDark,
    "--seg-label": c.label,
    "--seg-label-dark": c.labelDark,
    "--seg-border": c.border,
  };
}
